import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Clock } from "lucide-react";

type Settings = {
  retention_days: number;
  archive_before_delete: boolean;
  archive_retention_days: number;
  last_run_at: string | null;
  last_run_deleted: number;
  last_run_archived: number;
};

export function AuditLogRetentionPanel({ onPurged }: { onPurged?: () => void }) {
  const [s, setS] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | "save" | "purge">(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("audit_log_retention_settings" as any)
      .select("retention_days, archive_before_delete, archive_retention_days, last_run_at, last_run_deleted, last_run_archived")
      .maybeSingle();
    if (error) toast.error("Failed to load retention settings", { description: error.message });
    else setS(data as unknown as Settings);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!s) return;
    setBusy("save");
    const { error } = await supabase
      .from("audit_log_retention_settings" as any)
      .update({
        retention_days: s.retention_days,
        archive_before_delete: s.archive_before_delete,
        archive_retention_days: s.archive_retention_days,
      })
      .eq("id", true);
    setBusy(null);
    if (error) return toast.error("Save failed", { description: error.message });
    toast.success("Retention settings saved");
  };

  const purge = async () => {
    setBusy("purge");
    const { data, error } = await supabase.rpc("run_audit_log_purge" as any);
    setBusy(null);
    if (error) return toast.error("Purge failed", { description: error.message });
    const r = (data ?? {}) as { deleted?: number; archived?: number; archive_purged?: number };
    toast.success("Purge complete", {
      description: `Archived ${r.archived ?? 0} · Deleted ${r.deleted ?? 0} · Archive purged ${r.archive_purged ?? 0}`,
    });
    load();
    onPurged?.();
  };

  if (loading || !s) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading retention settings…
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Clock className="size-4" /> Audit log retention
          </h3>
          <p className="text-sm text-muted-foreground">
            Logs older than the retention window are {s.archive_before_delete ? "archived, then" : ""} deleted automatically every night.
          </p>
        </div>
        {s.last_run_at && (
          <div className="font-mono text-[10px] text-muted-foreground text-right">
            <div>last run · {new Date(s.last_run_at).toLocaleString()}</div>
            <div>archived {s.last_run_archived} · deleted {s.last_run_deleted}</div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="ret-days">Retention (days)</Label>
          <Input id="ret-days" type="number" min={1} max={3650}
            value={s.retention_days}
            onChange={(e) => setS({ ...s, retention_days: Math.max(1, Math.min(3650, Number(e.target.value) || 1)) })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="arc-days">Archive retention (days)</Label>
          <Input id="arc-days" type="number" min={1} max={3650}
            disabled={!s.archive_before_delete}
            value={s.archive_retention_days}
            onChange={(e) => setS({ ...s, archive_retention_days: Math.max(1, Math.min(3650, Number(e.target.value) || 1)) })} />
        </div>
        <div className="flex items-end gap-3">
          <div className="flex items-center gap-2">
            <Switch id="arc-on" checked={s.archive_before_delete}
              onCheckedChange={(v) => setS({ ...s, archive_before_delete: v })} />
            <Label htmlFor="arc-on">Archive before delete</Label>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={purge} disabled={!!busy}>
          {busy === "purge" ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
          Run purge now
        </Button>
        <Button onClick={save} disabled={!!busy}>
          {busy === "save" ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Save settings
        </Button>
      </div>
    </div>
  );
}
